import type * as React from "react";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export interface SelectableRadioOption<T extends string> {
  value: T;
  /** 굵게 표시되는 제목 (사업자명 등) */
  title: React.ReactNode;
  /** 제목 아래 보조 줄의 항목명 (예: "사업자등록번호") */
  metaLabel?: React.ReactNode;
  /** 보조 줄의 값 */
  metaValue?: React.ReactNode;
}

interface SelectableRadioListProps<T extends string> {
  options: SelectableRadioOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  className?: string;
}

/**
 * 라디오 + 제목 + 보조 줄로 이뤄진 단일 선택 목록. 선택된 행만 2px 테두리가 켜진다.
 * 배경·체크 아이콘이 없어 `SelectableCardGroup` 보다 항목이 많은 자리에 쓴다.
 *
 * Radix 라디오는 `<button>` 이라 `<label>` 로 감싸도 클릭이 전달되지 않아 행에 직접
 * 핸들러를 둔다 — 같은 값을 다시 넣는 것이라 라디오와 중복 호출돼도 무해하다.
 */
export function SelectableRadioList<T extends string>({
  options,
  value,
  onChange,
  className,
}: SelectableRadioListProps<T>) {
  return (
    <RadioGroup
      value={value ?? ""}
      onValueChange={(next) => onChange(next as T)}
      className={cn("flex w-full flex-col gap-2", className)}
    >
      {options.map((option) => {
        const isSelected = option.value === value;

        return (
          <div
            key={option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-4",
              isSelected
                ? "inset-border-radio-list-selected"
                : "inset-border-radio-list",
            )}
          >
            {/* 24px 자리에 20px 라디오를 중앙 정렬 — 디자인의 Radio 컨테이너 규격 */}
            <span className="flex size-6 shrink-0 items-center justify-center">
              <RadioGroupItem value={option.value} />
            </span>

            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="typo-heading-2-semibold truncate text-labels-primary">
                {option.title}
              </span>
              {(option.metaLabel || option.metaValue) && (
                <span className="typo-label-1-medium flex gap-1">
                  {option.metaLabel && (
                    <span className="text-labels-alternative">
                      {option.metaLabel}
                    </span>
                  )}
                  {option.metaValue && (
                    <span className="text-labels-primary">
                      {option.metaValue}
                    </span>
                  )}
                </span>
              )}
            </span>
          </div>
        );
      })}
    </RadioGroup>
  );
}

SelectableRadioList.displayName = "SelectableRadioList";
